import { BlockBlobClient } from "@azure/storage-blob";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  InputOnChangeData,
  mergeClasses,
  Select,
  Spinner,
  Text,
  Textarea,
  TextareaOnChangeData,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowDownload24Regular,
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
  DocumentRegular,
  Edit16Regular,
} from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { FileDropzone } from "components/fileDropzone/FileDropzone";
import { ChangeEvent, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedFileCardStyles,
} from "theme/sharedStyles";
import { prepareHeaderBytes } from "utils/blobUtils";
import { v4 as uuidv4 } from "uuid";
import * as XLSX from "xlsx";
import {
  addAttachment,
  deleteAttachment,
  deleteProject,
  getDownloadSignedUrl,
  getSignedUrl,
  updateAttachment,
} from "../../decoodaapi";
import {
  normalizeHeader,
  parseCSVColumns,
  parseXLSXColumns,
} from "../../utils/columnParsingUtils";
import { useEditProjectDialogStyles } from "./EditProjectDialog.styles";
import {
  IEditProjectDialogProps,
  ILastSavedValues,
  IPendingAttachment,
  WizardTab,
} from "./EditProjectDialog.types";
import { IAttachment, IProjectCollaborator } from "./projectCard.types";
import { formatExportFilename } from "./projectUtils";

const DELIMITER_OPTIONS = [
  { value: ",", label: "Comma (,)" },
  { value: ";", label: "Semicolon (;)" },
  { value: "\t", label: "Tab" },
  { value: "|", label: "Pipe (|)" },
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const VALID_MIME_TYPES = [
  "text/csv",
  "application/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const VALID_EXTENSIONS = [".csv", ".xlsx"];

export const getXLSXSheetNames = async (file: File): Promise<string[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  return workbook.SheetNames;
};

export const EditProjectDialog = (
  props: IEditProjectDialogProps,
): JSX.Element => {
  const { isOpen, project, onDismiss, onSave, existingProjects } = props;
  const dialogStyles = useEditProjectDialogStyles();
  const sharedContentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const fileCardStyles = useSharedFileCardStyles();

  const nameInputRef = useRef<HTMLInputElement>(null);
  const isEditMode = !!project;
  const currentUserId = useSelector(
    (state: RootState) => state.currentUserSlice.value?.id,
  );

  const initializeCollaborators = (): CustomOption[] => {
    if (isEditMode && project?.access) {
      return project.access
        .filter((c) => c.userId !== project.createdBy?.userId)
        .map((c) => ({
          id: c.userId,
          key: c.userId,
          text: c.displayName,
        }));
    }
    return [];
  };

  const [name, setName] = useState(
    project?.name || ConstantValues.EMPTY_STRING,
  );
  const [objective, setObjective] = useState(
    project?.objective || ConstantValues.EMPTY_STRING,
  );
  const [clientName, setClientName] = useState(
    project?.clientName || ConstantValues.EMPTY_STRING,
  );
  const [workdayCode, setWorkdayCode] = useState(
    project?.workdayCode || ConstantValues.EMPTY_STRING,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [selectedCollaborators, setSelectedCollaborators] = useState<
    CustomOption[]
  >(initializeCollaborators());

  const [activeTab, setActiveTab] = useState<WizardTab>("details");
  const [draftProjectId, setDraftProjectId] = useState<string | null>(null);
  const [draftProjectName, setDraftProjectName] = useState<string | null>(null);
  const [lastSavedValues, setLastSavedValues] =
    useState<ILastSavedValues | null>(null);

  const [attachments, setAttachments] = useState<IAttachment[]>(
    project?.attachments || [],
  );
  const [pendingAttachments, setPendingAttachments] = useState<
    IPendingAttachment[]
  >([]);
  const [hasAttemptedCreate, setHasAttemptedCreate] = useState(false);

  const [editingAttachmentId, setEditingAttachmentId] = useState<string | null>(
    null,
  );
  const [editingAttachmentState, setEditingAttachmentState] = useState<{
    sheetName: string;
    columns: string[];
    contentColumn: string;
  } | null>(null);
  const [editingAttachmentSheetNames, setEditingAttachmentSheetNames] =
    useState<string[]>([]);

  const handleChange =
    (setter: (v: string) => void, fieldKey?: string) =>
    (_: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
      setter(data.value);
      if (fieldKey && fieldErrors[fieldKey]) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldKey]: ConstantValues.EMPTY_STRING,
        }));
      }
    };

  const handleTextareaChange =
    (setter: (v: string) => void) =>
    (_: ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => {
      setter(data.value);
      if (fieldErrors.general) {
        setFieldErrors((prev) => ({
          ...prev,
          general: ConstantValues.EMPTY_STRING,
        }));
      }
    };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = "Project name is required.";
    }

    const nameUnchanged =
      isEditMode &&
      name.trim().toLowerCase() === project?.name.trim().toLowerCase();

    if (!nameUnchanged) {
      const conflict = existingProjects.some(
        (p) =>
          p.name.trim().toLowerCase() === name.trim().toLowerCase() &&
          (!isEditMode || p.id !== project?.id),
      );
      if (conflict) {
        errors.name = "A project with this name already exists.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.name) {
        nameInputRef.current?.focus();
      }
      return false;
    }

    return true;
  };

  const handleSave = async (): Promise<void> => {
    if (!isEditMode && draftProjectId) {
      if (!validate()) {
        setActiveTab("details");
        return;
      }
      setHasAttemptedCreate(true);

      if (attachments.length === 0 && pendingAttachments.length === 0) {
        setFieldErrors({ attachments: "At least one attachment is required." });
        return;
      }

      const hasIncompleteAttachments = pendingAttachments.some(
        (a) => !a.contentColumn,
      );

      if (hasIncompleteAttachments) {
        setFieldErrors({
          attachments: "Please select a content column for all attachments.",
        });
        return;
      }

      if (pendingAttachments.length > 0) {
        setFieldErrors({
          attachments:
            "Please upload all pending attachments or remove them before creating.",
        });
        return;
      }
      onDismiss(true);
      return;
    }

    if (!validate()) {
      return;
    }

    if (attachments.length === 0 && pendingAttachments.length === 0) {
      setFieldErrors({ attachments: "At least one attachment is required." });
      return;
    }

    setHasAttemptedCreate(true);

    const hasIncompleteAttachments = pendingAttachments.some(
      (a) => !a.contentColumn,
    );
    if (hasIncompleteAttachments) {
      setFieldErrors({
        attachments: "Please select a content column for all attachments.",
      });
      return;
    }

    setIsLoading(true);
    try {
      const collaborators: IProjectCollaborator[] = selectedCollaborators.map(
        (c) => ({
          userId: c.id || ConstantValues.EMPTY_STRING,
          displayName: c.text,
        }),
      );

      await onSave({
        id: project?.id,
        name: name.trim(),
        objective: objective.trim(),
        clientName: clientName.trim(),
        workdayCode: workdayCode.trim(),
        access: collaborators,
      });

      setIsLoading(false);
      onDismiss(true);
    } catch (e) {
      console.error("Error saving project:", e);
      setFieldErrors({
        general: "An error occurred while saving. Please try again.",
      });
      setIsLoading(false);
    }
  };

  const handleSelectedCollaborators = (opts: CustomOption[]) => {
    setSelectedCollaborators(opts);
    if (fieldErrors.general) {
      setFieldErrors((prev) => ({
        ...prev,
        general: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    const projectId = project?.id || draftProjectId;
    if (!projectId) return;

    try {
      await deleteAttachment({ projectId, attachmentId });
      setAttachments(attachments.filter((a) => a.id !== attachmentId));
    } catch (error) {
      console.error("Error deleting attachment:", error);
      setFieldErrors({
        attachments: "Failed to delete attachment. Please try again.",
      });
    }
  };

  const handleTabSelect = (tabKey: string) => {
    const newTab = tabKey as WizardTab;

    if (!isEditMode && newTab === "attachments") {
      handleNextClick();
      return;
    }

    setActiveTab(newTab);
  };

  const validateDetailsTab = (): boolean => {
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = "Project name is required.";
    }

    const nameUnchanged =
      isEditMode &&
      name.trim().toLowerCase() === project?.name.trim().toLowerCase();

    if (!nameUnchanged) {
      const conflict = existingProjects.some(
        (p) =>
          p.name.trim().toLowerCase() === name.trim().toLowerCase() &&
          (!isEditMode || p.id !== project?.id) &&
          p.name.trim().toLowerCase() !== draftProjectName?.toLowerCase(),
      );
      if (conflict) {
        errors.name = "A project with this name already exists.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const hasUnsavedChanges = (): boolean => {
    if (!lastSavedValues) return true;

    const collaboratorIds = selectedCollaborators.map((c) => c.id).sort();
    const savedCollaboratorIds = lastSavedValues.collaborators
      .map((c) => c.id)
      .sort();

    return (
      name.trim() !== lastSavedValues.name ||
      objective.trim() !== lastSavedValues.objective ||
      clientName.trim() !== lastSavedValues.clientName ||
      workdayCode.trim() !== lastSavedValues.workdayCode ||
      JSON.stringify(collaboratorIds) !== JSON.stringify(savedCollaboratorIds)
    );
  };

  const handleNextClick = async () => {
    if (!validateDetailsTab()) {
      return;
    }

    const collaborators: IProjectCollaborator[] = selectedCollaborators.map(
      (c) => ({
        userId: c.id || ConstantValues.EMPTY_STRING,
        displayName: c.text,
      }),
    );

    if (draftProjectId && !hasUnsavedChanges()) {
      setActiveTab("attachments");
      return;
    }

    setIsLoading(true);
    try {
      if (draftProjectId) {
        await onSave(
          {
            id: draftProjectId,
            name: name.trim(),
            objective: objective.trim(),
            clientName: clientName.trim(),
            workdayCode: workdayCode.trim(),
            access: collaborators,
          },
          { skipRefresh: true },
        );
        setDraftProjectName(name.trim());
        setLastSavedValues({
          name: name.trim(),
          objective: objective.trim(),
          clientName: clientName.trim(),
          workdayCode: workdayCode.trim(),
          collaborators: [...selectedCollaborators],
        });
        setActiveTab("attachments");
      } else {
        const projectId = await onSave(
          {
            name: name.trim(),
            objective: objective.trim(),
            clientName: clientName.trim(),
            workdayCode: workdayCode.trim(),
            access: collaborators,
          },
          { skipRefresh: true },
        );

        if (projectId) {
          setDraftProjectId(projectId);
          setDraftProjectName(name.trim());
          setLastSavedValues({
            name: name.trim(),
            objective: objective.trim(),
            clientName: clientName.trim(),
            workdayCode: workdayCode.trim(),
            collaborators: [...selectedCollaborators],
          });
          setActiveTab("attachments");
        } else {
          setFieldErrors({
            general: "Failed to create project. Please try again.",
          });
        }
      }
    } catch (e) {
      console.error("Error saving draft project:", e);
      setFieldErrors({
        general:
          "An error occurred while saving the project. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    setActiveTab("details");
  };

  const handleCancelWizard = async () => {
    const triggerElement = document.activeElement as HTMLElement | null;
    if (draftProjectId) {
      setIsLoading(true);
      try {
        await deleteProject({ projectId: draftProjectId });
      } catch (error) {
        console.error("Error deleting draft project on cancel:", error);
      }
      setIsLoading(false);
    }
    onDismiss(false);
    triggerElement?.focus();
  };

  const handleDelimiterChangeForCreate = async (
    attachmentId: string,
    delimiter: string,
  ) => {
    const attachment = pendingAttachments.find((a) => a.id === attachmentId);
    if (!attachment) return;

    const ext = attachment.file.name
      .substring(attachment.file.name.lastIndexOf("."))
      .toLowerCase();
    const columns =
      ext === ".xlsx"
        ? await parseXLSXColumns(
            attachment.file,
            attachment.selectedSheet || undefined,
          )
        : parseCSVColumns(await attachment.file.text(), delimiter);

    setPendingAttachments((prev) =>
      prev.map((a) =>
        a.id === attachmentId
          ? {
              ...a,
              delimiter,
              columns,
              contentColumn: columns[0] || ConstantValues.EMPTY_STRING,
            }
          : a,
      ),
    );
  };

  const handleSheetChangeForCreate = async (
    attachmentId: string,
    sheet: string,
  ) => {
    const attachment = pendingAttachments.find((a) => a.id === attachmentId);
    if (!attachment) return;

    const columns = await parseXLSXColumns(attachment.file, sheet || undefined);

    setPendingAttachments((prev) =>
      prev.map((a) =>
        a.id === attachmentId
          ? {
              ...a,
              selectedSheet: sheet,
              columns,
              contentColumn: columns[0] || ConstantValues.EMPTY_STRING,
            }
          : a,
      ),
    );
  };

  const handleContentColumnChangeForCreate = async (
    attachmentId: string,
    column: string,
  ) => {
    if (!column) {
      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.id === attachmentId ? { ...a, contentColumn: column } : a,
        ),
      );
      return;
    }

    const attachmentToUpload = pendingAttachments.find(
      (a) => a.id === attachmentId,
    );
    if (!attachmentToUpload || attachmentToUpload.isUploading) return;

    await handleUploadPendingAttachmentWithColumn(
      attachmentId,
      column,
      attachmentToUpload,
    );
  };

  const handleUploadPendingAttachmentWithColumn = async (
    attachmentId: string,
    contentColumn: string,
    attachmentParam: IPendingAttachment,
  ) => {
    const projectId = isEditMode ? project?.id : draftProjectId;
    if (!projectId) return;

    const attachment = attachmentParam;
    if (!attachment || attachment.isUploading) return;

    setPendingAttachments((prev) =>
      prev.map((a) =>
        a.id === attachmentId ? { ...a, contentColumn, isUploading: true } : a,
      ),
    );

    try {
      const fileData = await prepareHeaderBytes(attachment.file);
      const { sas, containerName, blobName } = await getSignedUrl({
        filename: attachment.file.name,
        projectId: projectId,
        fileData,
      });

      const sasUrl = new URL(sas);
      const blobUrl = `${sasUrl.origin}/${containerName}/${blobName}${sasUrl.search}`;
      const blobClient = new BlockBlobClient(blobUrl);
      await blobClient.uploadData(attachment.file, {
        blobHTTPHeaders: { blobContentType: attachment.file.type },
      });

      const result = await addAttachment({
        projectId: projectId,
        filename: attachment.file.name,
        fileUrl: blobName,
        delimiter: attachment.delimiter,
        contentColumn: contentColumn,
        columns: attachment.columns,
        sheetName: attachment.selectedSheet || undefined,
      });

      setAttachments((prev) => [...prev, result.attachment]);
      setPendingAttachments((prev) =>
        prev.filter((a) => a.id !== attachmentId),
      );
      setFieldErrors((prev) => ({
        ...prev,
        attachments: ConstantValues.EMPTY_STRING,
      }));
    } catch (error) {
      console.error("Error uploading attachment:", error);
      setFieldErrors({
        attachments: "Failed to upload attachment. Please try again.",
      });
      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.id === attachmentId ? { ...a, isUploading: false } : a,
        ),
      );
    }
  };

  const handleRemovePendingAttachment = (attachmentId: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  };

  const handleDropzoneFiles = async (files: File[]): Promise<void> => {
    const projectId = isEditMode ? project?.id : draftProjectId;
    if (files.length === 0) return;
    if (!projectId) {
      setFieldErrors((prev) => ({
        ...prev,
        attachments: "Please complete project details first.",
      }));
      return;
    }
    await processMultipleFiles(files);
  };

  const processMultipleFiles = async (files: File[]) => {
    const newPendingAttachments: IPendingAttachment[] = [];
    const skippedFiles: string[] = [];

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        skippedFiles.push(
          `${file.name} (exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit)`,
        );
        continue;
      }

      if (!VALID_MIME_TYPES.includes(file.type)) {
        skippedFiles.push(`${file.name} (invalid file type)`);
        continue;
      }

      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (!VALID_EXTENSIONS.includes(ext)) {
        skippedFiles.push(`${file.name} (invalid extension)`);
        continue;
      }

      try {
        const isXlsx = ext === ".xlsx";
        let columns: string[];
        const defaultDelimiter = ",";
        let sheetNames: string[] = [];
        let selectedSheet: string = ConstantValues.EMPTY_STRING;

        if (isXlsx) {
          sheetNames = await getXLSXSheetNames(file);
          selectedSheet = sheetNames[0] ?? ConstantValues.EMPTY_STRING;
          columns = await parseXLSXColumns(file, selectedSheet || undefined);
        } else {
          const content = await file.text();
          if (content.length === 0) {
            skippedFiles.push(`${file.name} (empty file)`);
            continue;
          }
          columns = parseCSVColumns(content, defaultDelimiter);
        }

        if (columns.length === 0) {
          skippedFiles.push(`${file.name} (could not parse columns)`);
          continue;
        }

        newPendingAttachments.push({
          id: uuidv4(),
          file,
          delimiter: defaultDelimiter,
          columns,
          contentColumn: ConstantValues.EMPTY_STRING,
          isUploading: false,
          sheetNames,
          selectedSheet,
        });
      } catch (error) {
        console.error(`Error reading file ${file.name}:`, error);
        skippedFiles.push(`${file.name} (read error)`);
      }
    }

    setFieldErrors((prev) => ({
      ...prev,
      attachments:
        skippedFiles.length > 0
          ? `Skipped ${skippedFiles.length} file(s): ${skippedFiles.join(", ")}`
          : ConstantValues.EMPTY_STRING,
    }));

    if (newPendingAttachments.length === 0 && files.length > 0) {
      if (skippedFiles.length === 0) {
        setFieldErrors((prev) => ({
          ...prev,
          attachments: "Only .csv and .xlsx files are allowed.",
        }));
      }
      return;
    }

    setPendingAttachments((prev) => [...prev, ...newPendingAttachments]);
    setHasAttemptedCreate(false);
    setFieldErrors((prev) => ({ ...prev, attachments: "" }));
  };

  const isSavingDraft = isLoading && !isEditMode && activeTab === "details";

  const renderProjectDetailsContent = () => (
    <div className={sharedContentStyles.tabContent}>
      {isSavingDraft && (
        <div className={sharedContentStyles.formLoadingOverlay}>
          <Spinner label="Preparing..." />
        </div>
      )}
      <Field
        label="Project Name"
        required
        validationState={fieldErrors.name ? "error" : undefined}
        validationMessage={fieldErrors.name}
      >
        <Input
          ref={nameInputRef}
          value={name}
          onChange={handleChange(setName, "name")}
          disabled={isSavingDraft}
          placeholder="Enter project name..."
        />
      </Field>

      <Field label="Objective">
        <Textarea
          value={objective}
          maxLength={1024}
          rows={3}
          onChange={handleTextareaChange(setObjective)}
          placeholder="Describe the project objective..."
          disabled={isSavingDraft}
        />
      </Field>

      <Field label="Client Name">
        <Input
          value={clientName}
          onChange={handleChange(setClientName)}
          placeholder="Enter client name"
          disabled={isSavingDraft}
        />
      </Field>

      <Field label="Project Workday Code">
        <Input
          value={workdayCode}
          onChange={handleChange(setWorkdayCode)}
          placeholder="Enter Workday code"
          disabled={isSavingDraft}
        />
      </Field>

      <Field label="Collaborators">
        <PeoplePicker
          id="collaborators"
          handleSelectedItems={handleSelectedCollaborators}
          disabled={isSavingDraft}
          selectedOptions={selectedCollaborators}
          hasOnlyOurUsers={true}
        />
      </Field>

      {fieldErrors.general && (
        <div className={sharedContentStyles.feedbackError} role="alert">
          {fieldErrors.general}
        </div>
      )}
    </div>
  );

  const renderCreateAttachmentsContent = () => (
    <div className={dialogStyles.attachmentContainer}>
      <Field
        validationState={fieldErrors.attachments ? "error" : "none"}
        validationMessage={fieldErrors.attachments}
      >
        <FileDropzone
          onFilesSelected={handleDropzoneFiles}
          acceptedExtensions={VALID_EXTENSIONS}
          multiple
          onValidationError={(msg) =>
            setFieldErrors((prev) => ({ ...prev, attachments: msg }))
          }
        />
      </Field>

      {(attachments.length > 0 || pendingAttachments.length > 0) && (
        <div className={dialogStyles.attachmentListContainer}>
          {attachments.map((attachment) => {
            const isEditing = editingAttachmentId === attachment.id;
            return (
              <div key={attachment.id} className={fileCardStyles.fileCard}>
                <DocumentRegular className={fileCardStyles.fileIcon} />
                <div className={fileCardStyles.fileInfo}>
                  <Tooltip
                    content={attachment.filename}
                    relationship="description"
                  >
                    <Text
                      size={200}
                      weight="semibold"
                      className={fileCardStyles.fileName}
                    >
                      {attachment.filename}
                    </Text>
                  </Tooltip>
                  {!isEditing && (
                    <Text size={100} className={fileCardStyles.fileStatus}>
                      {attachment.sheetName
                        ? `Sheet: ${attachment.sheetName} · `
                        : ""}
                      Content Column: {attachment.contentColumn || "N/A"}
                    </Text>
                  )}
                  {isEditing && editingAttachmentState && (
                    <div className={dialogStyles.fieldsRow}>
                      {editingAttachmentSheetNames.length > 0 && (
                        <div>
                          <Field label="Sheet" size="small">
                            <Select
                              value={editingAttachmentState.sheetName}
                              onChange={(_, data) =>
                                handleExistingAttachmentSheetChange(
                                  attachment,
                                  data.value,
                                )
                              }
                              size="small"
                              className={dialogStyles.fieldSelect}
                            >
                              {editingAttachmentSheetNames.map(
                                (sheet: string) => (
                                  <option key={sheet} value={sheet}>
                                    {sheet}
                                  </option>
                                ),
                              )}
                            </Select>
                          </Field>
                        </div>
                      )}
                      <div>
                        <Field
                          label="Content Column"
                          size="small"
                          className={dialogStyles.fieldsContentColumn}
                        >
                          <Select
                            value={editingAttachmentState.contentColumn}
                            onChange={(_, data) =>
                              setEditingAttachmentState((prev) =>
                                prev
                                  ? { ...prev, contentColumn: data.value }
                                  : prev,
                              )
                            }
                            size="small"
                            className={dialogStyles.fieldSelectWithMin}
                          >
                            {editingAttachmentState.columns.map(
                              (col: string) => (
                                <option
                                  key={col}
                                  value={col}
                                  title={col}
                                  className={dialogStyles.fieldOption}
                                >
                                  {col.length > 25
                                    ? `${col.substring(0, 25)}...`
                                    : col}
                                </option>
                              ),
                            )}
                          </Select>
                        </Field>
                      </div>
                    </div>
                  )}
                </div>
                <div className={fileCardStyles.fileActions}>
                  {!isEditing && (
                    <>
                      <Tooltip content="Edit sheet/column" relationship="label">
                        <Button
                          appearance="subtle"
                          icon={<Edit16Regular />}
                          className={fileCardStyles.fileActionButton}
                          onClick={() => handleStartEditAttachment(attachment)}
                        />
                      </Tooltip>
                      <Tooltip
                        content="Download attachment"
                        relationship="label"
                      >
                        <Button
                          appearance="subtle"
                          icon={<ArrowDownload24Regular />}
                          className={fileCardStyles.fileDownloadButton}
                          onClick={() => handleDownloadAttachment(attachment)}
                        />
                      </Tooltip>
                      <Tooltip content="Delete attachment" relationship="label">
                        <Button
                          appearance="subtle"
                          icon={<DeleteRegular />}
                          className={fileCardStyles.fileDeleteButton}
                          onClick={() => handleDeleteAttachment(attachment.id)}
                        />
                      </Tooltip>
                    </>
                  )}
                  {isEditing && (
                    <>
                      <Tooltip content="Save changes" relationship="label">
                        <Button
                          appearance="subtle"
                          icon={<CheckmarkRegular />}
                          className={fileCardStyles.fileActionButton}
                          onClick={() => handleSaveEditAttachment(attachment)}
                        />
                      </Tooltip>
                      <Tooltip content="Cancel" relationship="label">
                        <Button
                          appearance="subtle"
                          icon={<DismissRegular />}
                          className={fileCardStyles.fileActionButton}
                          onClick={handleCancelEditAttachment}
                        />
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {pendingAttachments.map((attachment) => (
            <div key={attachment.id} className={dialogStyles.pendingCard}>
              <div className={sharedContentStyles.headerRow}>
                <div className={dialogStyles.fileInfoContainer}>
                  <DocumentRegular className={dialogStyles.fileIconSmall} />
                  <Text
                    size={200}
                    weight="semibold"
                    className={dialogStyles.fileName}
                  >
                    {attachment.file.name}
                  </Text>
                  <Text size={100} className={dialogStyles.fileSize}>
                    {(attachment.file.size / (1024 * 1024)).toFixed(1)} MB
                  </Text>
                </div>
                <div className={dialogStyles.actionsContainer}>
                  {attachment.isUploading && <Spinner size="tiny" />}
                  <Tooltip
                    content={
                      attachment.isUploading ? "Cancel upload" : "Remove"
                    }
                    relationship="label"
                  >
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DeleteRegular />}
                      onClick={() =>
                        handleRemovePendingAttachment(attachment.id)
                      }
                    />
                  </Tooltip>
                </div>
              </div>

              <div className={dialogStyles.fieldsRow}>
                {!attachment.file.name.toLowerCase().endsWith(".xlsx") && (
                  <div>
                    <Field label="Delimiter" size="small">
                      <Select
                        value={attachment.delimiter}
                        onChange={(_, data) =>
                          handleDelimiterChangeForCreate(
                            attachment.id,
                            data.value,
                          )
                        }
                        disabled={attachment.isUploading}
                        size="small"
                        className={dialogStyles.fieldSelect}
                      >
                        {DELIMITER_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                )}

                {attachment.sheetNames.length > 0 && (
                  <div>
                    <Field label="Sheet" size="small">
                      <Select
                        value={attachment.selectedSheet}
                        onChange={(_, data) =>
                          handleSheetChangeForCreate(attachment.id, data.value)
                        }
                        disabled={attachment.isUploading}
                        size="small"
                        className={dialogStyles.fieldSelect}
                      >
                        {attachment.sheetNames.map((sheet) => (
                          <option key={sheet} value={sheet}>
                            {sheet}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                )}

                <div>
                  <Field
                    label="Content Column"
                    size="small"
                    required
                    validationState={
                      hasAttemptedCreate && !attachment.contentColumn
                        ? "error"
                        : undefined
                    }
                    validationMessage={
                      hasAttemptedCreate && !attachment.contentColumn
                        ? "Required"
                        : undefined
                    }
                    className={dialogStyles.fieldsContentColumn}
                  >
                    <Select
                      value={attachment.contentColumn}
                      onChange={(_, data) =>
                        handleContentColumnChangeForCreate(
                          attachment.id,
                          data.value,
                        )
                      }
                      disabled={attachment.isUploading}
                      size="small"
                      className={dialogStyles.fieldSelectWithMin}
                    >
                      <option value={ConstantValues.EMPTY_STRING}>
                        Select column...
                      </option>
                      {attachment.columns.map((col) => (
                        <option
                          key={col}
                          value={col}
                          title={col}
                          className={dialogStyles.fieldOption}
                        >
                          {col.length > 25 ? `${col.substring(0, 25)}...` : col}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDialogFooter = () => {
    if (isLoading && isEditMode && activeTab === "details") return null;

    if (activeTab === "details") {
      const hasIncompleteDetails = !name.trim();
      return (
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={
              isSavingDraft ? <Spinner size="tiny" /> : <ArrowRightRegular />
            }
            tooltip="Next"
            onClick={
              isEditMode ? () => setActiveTab("attachments") : handleNextClick
            }
            disabled={isEditMode ? isLoading : isSavingDraft}
            dimmed={hasIncompleteDetails}
          />
        </div>
      );
    }

    const saveLabel = isEditMode ? "Update Project" : "Create Project";
    const hasNoAttachments =
      attachments.length === 0 && pendingAttachments.length === 0;
    return (
      <div className={footerStyles.dialogFooter}>
        <DialogFooterAction
          icon={<ArrowLeftRegular />}
          tooltip="Back"
          onClick={isEditMode ? () => setActiveTab("details") : handleBackClick}
          disabled={isLoading}
        />
        <DialogFooterAction
          icon={<CheckmarkRegular />}
          tooltip={saveLabel}
          onClick={handleSave}
          disabled={isLoading}
          dimmed={hasNoAttachments}
        />
      </div>
    );
  };

  const handleDownloadAttachment = async (attachment: IAttachment) => {
    try {
      const fileWithUrl = await getDownloadSignedUrl({
        filename: attachment.fileUrl,
      });
      if (!fileWithUrl?.sas) {
        throw new Error("Failed to get download URL");
      }

      const response = await fetch(fileWithUrl.sas);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const source = attachment.filename
        .replace(/\.[^/.]+$/, "")
        .replace(/\s+/g, "_");
      const extension = attachment.filename.split(".").pop() || "csv";
      link.download = formatExportFilename(project?.name, source, extension);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      setFieldErrors({
        attachments: "Failed to download attachment. Please try again.",
      });
    }
  };

  const handleStartEditAttachment = async (attachment: IAttachment) => {
    setEditingAttachmentId(attachment.id);
    setEditingAttachmentState({
      sheetName: attachment.sheetName ?? "",
      columns: attachment.columns,
      contentColumn: attachment.contentColumn ?? "",
    });

    if (attachment.filename.toLowerCase().endsWith(".xlsx")) {
      try {
        const fileWithUrl = await getDownloadSignedUrl({
          filename: attachment.fileUrl,
        });
        if (!fileWithUrl?.sas) {
          throw new Error("Failed to get download URL");
        }
        const response = await fetch(fileWithUrl.sas);
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status}`);
        }
        const buffer = await response.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        setEditingAttachmentSheetNames(workbook.SheetNames);
      } catch (error) {
        console.error("Error fetching sheet names:", error);
        setEditingAttachmentSheetNames([]);
      }
    }
  };

  const handleCancelEditAttachment = () => {
    setEditingAttachmentId(null);
    setEditingAttachmentState(null);
    setEditingAttachmentSheetNames([]);
  };

  const handleExistingAttachmentSheetChange = async (
    attachment: IAttachment,
    newSheet: string,
  ) => {
    try {
      const fileWithUrl = await getDownloadSignedUrl({
        filename: attachment.fileUrl,
      });
      if (!fileWithUrl?.sas) {
        throw new Error("Failed to get download URL");
      }
      const response = await fetch(fileWithUrl.sas);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      const buffer = await response.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[newSheet];
      const rows = sheet
        ? ((XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 })[0] as (
            string | number | null
          )[]) ?? [])
        : [];
      const newColumns: string[] = rows
        .map((col) => normalizeHeader(String(col ?? "")))
        .filter(Boolean);
      setEditingAttachmentState({
        sheetName: newSheet,
        columns: newColumns,
        contentColumn: newColumns[0] ?? "",
      });
    } catch (error) {
      console.error("Error fetching sheet columns:", error);
      setFieldErrors({
        attachments: "Failed to load sheet columns. Please try again.",
      });
    }
  };

  const handleSaveEditAttachment = async (attachment: IAttachment) => {
    const projectId = project?.id || draftProjectId;
    if (!projectId || !editingAttachmentState) return;

    try {
      await updateAttachment({
        projectId,
        attachmentId: attachment.id,
        delimiter: attachment.delimiter,
        sheetName: editingAttachmentState.sheetName || undefined,
        contentColumn: editingAttachmentState.contentColumn,
        columns: editingAttachmentState.columns,
      });
      setAttachments((prev: IAttachment[]) =>
        prev.map((a: IAttachment) =>
          a.id === attachment.id
            ? {
                ...a,
                sheetName: editingAttachmentState.sheetName || undefined,
                contentColumn: editingAttachmentState.contentColumn,
                columns: editingAttachmentState.columns,
              }
            : a,
        ),
      );
      setEditingAttachmentId(null);
      setEditingAttachmentState(null);
    } catch (error) {
      console.error("Error updating attachment:", error);
      setFieldErrors({
        attachments: "Failed to update attachment. Please try again.",
      });
    }
  };

  const handleDismiss =
    !isEditMode && draftProjectId ? handleCancelWizard : () => onDismiss(false);

  return (
    <Dialog
      open={isOpen}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open && !isLoading) handleDismiss();
      }}
    >
      <DialogSurface className={footerStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={handleDismiss}
                disabled={isLoading}
                tabIndex={isLoading ? -1 : undefined}
              />
            }
          >
            {isEditMode ? "Edit Project" : "Create Project"}
          </DialogTitle>
          <DialogContent className={sharedContentStyles.tabbedDialogContent}>
            {isLoading && isEditMode && activeTab === "details" ? (
              <div className={sharedContentStyles.loadingContainer}>
                <Spinner label="Saving project..." />
              </div>
            ) : (
              <>
                <DialogTabs
                  tabs={[
                    { key: "details", label: "Project Details" },
                    { key: "attachments", label: "Attachments" },
                  ]}
                  activeTab={activeTab}
                  onTabClick={handleTabSelect}
                />
                <div
                  className={mergeClasses(
                    sharedContentStyles.contentContainer,
                    sharedContentStyles.scrollableContent,
                  )}
                >
                  {activeTab === "details" && renderProjectDetailsContent()}
                  {activeTab === "attachments" &&
                    renderCreateAttachmentsContent()}
                </div>
              </>
            )}
          </DialogContent>
        </DialogBody>
        {renderDialogFooter()}
      </DialogSurface>
    </Dialog>
  );
};
